<script setup lang="ts">
// @ts-nocheck
import CartSummary from './CartSummary.vue';
import {label} from '../../mixins/translations.js';
import {useCartStore} from "../../stores/cart";
import type {DebtorContactResponse} from "../../../responses/DebtorContactResponse.ts";
import {computed, onMounted, watch} from "vue";
import {useStorage} from '@vueuse/core'
import Input from "../../../backend/ui/Input.vue";
import {Money} from "../../../utils/money.ts";
import {ref, type Ref} from "vue";
import Errors from "../Errors.vue";
import type {AddressResponse} from "../../../responses/AddressResponse.ts";
import {isEmpty} from "lodash";
import CartEmptyMessage from "./CartEmptyMessage.vue";

const cartStore = useCartStore();

interface AddressEntry {
    country: '',
    postal: '',
    number: '',
}

type DeliveryMethod = string
type PaymentMethod = string

interface Props {
    user?: DebtorContactResponse
    paymentMethods?: object[]
    route: string
    loginRoute?: string
    countries: { [key: string]: string },
    errors: object,
    deliveryAddresses?: AddressResponse[],
    invoiceAddresses?: AddressResponse[],
}

const props = withDefaults<Props>(defineProps<Props>(), {})


//https://vueuse.org/core/useStorage/
//DATA saven in session storage, if not filled, fill from user, fill
const form = useStorage('cart-form', {
    email: props.user?.email ?? '',
    payment_method: 'ideal',
    delivery_method: null,
    invoice_address: {},
    delivery_address: {},
    invoice_same_as_delivery: true,
    create_account: true,
    remarks: ''
}, sessionStorage, {mergeDefaults: true})

const csrf: Ref<string | null> = ref('');

const updateCountry = (countryCode) => {
    if (countryCode !== cartStore.delivery_country_code.value) {
        cartStore.updateCountryCode(countryCode);

    }
}

const changeDeliveryMethod = async (delivery) => {
    await cartStore.updateDeliveryMethod({delivery_method: delivery}).then((response) => {
        //donothing? nu in cart store, potentieel toast melding
    })
}

watch(
    () => form.value.invoice_same_as_delivery,
    (newValue, oldValue) => {
        if (newValue) {
            updateCountry(form.value.invoice_address?.country);
        } else {
            updateCountry(form.value.delivery_address?.country);
        }
    },
    {deep: true}
);

watch(
    () => form.value.invoice_address,
    (newAddress, oldAddress) => {
        if (form.value.invoice_same_as_delivery) {
            if (newAddress?.country !== cartStore.delivery_country_code) {
                updateCountry(newAddress?.country);
            }
        }
    },
    {deep: true}
);

watch(
    () => form.value.delivery_address,
    (newAddress, oldAddress) => {
        if (newAddress?.country !== cartStore.delivery_country_code) {
            updateCountry(newAddress.country);
        }
    },
    {deep: true}
);

onMounted(() => {
    csrf.value = document.querySelector('meta[name="csrf-token"]').getAttribute("content");//set csrf from meta
    if (props.user) {
        //https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Logical_OR_assignment
        form.value.email ||= props.user.email;
        form.value.phone_number ||= props.user?.debtor?.phone;
        form.value.vat_number ||= props.user?.debtor?.vat_number;
        //Preset addressses
        if (props.deliveryAddresses.length && isEmpty(form.value.delivery_address)) {
            form.value.delivery_address = props.deliveryAddresses[0];
        }
        if (props.invoiceAddresses.length && isEmpty(form.value.invoice_address)) {
            form.value.invoice_address = props.invoiceAddresses[0];
        }
    }
    cartStore.loadDeliveryMethods();
})

const invoiceNames = { //field names, important for use in blade forms
    id: "address_invoice",
    company: "company",
    salutation: "salutation",
    first_name: "first_name",
    insertion: "insertion",
    last_name: "last_name",
    postal: "postal",
    house_number: "house_number",
    house_number_addition: "house_number_addition",
    street: "street",
    city: "city",
    country: "country",
    address_line_1: 'address_line_1',
    address_line_2: 'address_line_2',
    type: 'invoice',
}

const deliveryNames = { //field names, important for use in blade forms
    id: "address_delivery",
    company: "delivery_company",
    salutation: "delivery_salutation",
    first_name: "delivery_first_name",
    insertion: "delivery_insertion",
    last_name: "delivery_last_name",
    postal: "delivery_postal",
    house_number: "delivery_house_number",
    house_number_addition: "delivery_house_number_addition",
    street: "delivery_street",
    city: "delivery_city",
    country: "delivery_country",
    address_line_1: 'delivery_address_line_1',
    address_line_2: 'delivery_address_line_2',
    type: 'delivery',
}
</script>
<template>
    <div v-if="cartStore.itemCount === 0 && !cartStore.isLoadingFull" class="py-32">
        <CartEmptyMessage class="max-w-2xl mx-auto"/>
    </div>
    <section class="max-w-[1000px] mx-auto md:px-0 px-5 gap-12 my-12" v-else>
        <div class="w-full">
            <h2 class="text-3xl font-bold tracking-tight text-gray-900">
                {{ label('site.cart.title') }}
            </h2>
        </div>
        <form method="POST" :action="route" class="lg:grid lg:grid-cols-2 lg:gap-x-12 xl:gap-x-16 w-full mt-10">
            <input v-model="csrf" type="hidden" name="_token"/>
            <div>
                <div class="mb-4 pb-4 border-b border-gray-200 space-y-3">
                    <h2 class="text-lg font-bold text-gray-900">
                        {{ label('site.cart.contact_information') }}
                    </h2>

                    <div class="mt-4 space-y-1" v-if="!user">
                        <label class="text-sm text-gray-600">
                            {{ label('site.account.email') }}
                            <span class="text-error-500" :title="label('required')">*</span>
                        </label>
                        <input
                            class="appearance-none border border-gray-300 p-2 rounded-md shadow-xs checked:bg-gray-500 checked:text-gray-500 disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed focus:border-gray-950 focus:ring-gray-950 block w-full sm:text-sm"
                            type="email" id="email" name="email" autocomplete="email" v-model="form.email" required>
                        <Errors :errors="errors?.email"/>
                        <p class="text-sm text-gray-700">
                            {{ label('site.cart.existing_account') }}
                            <a :href="loginRoute" class="btn btn-link text-gray-950 font-bold underline"
                               v-text="label('site.button.login')"></a>
                        </p>
                    </div>

                    <div class="mt-4 space-y-1">
                        <label class="text-sm text-gray-600">
                            {{ label('site.account.phone') }}
                            <span class="text-error-500" :title="label('required')">*</span>
                        </label>
                        <input
                            class="appearance-none border border-gray-300 p-2 rounded-md shadow-xs checked:bg-gray-500 checked:text-gray-500 disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed focus:border-gray-950 focus:ring-gray-950 block w-full sm:text-sm"
                            type="text" id="phone_number" name="phone_number" v-model="form.phone_number">
                        <Errors :errors="errors?.phone_number"/>
                    </div>
                    <div v-if="form.invoice_address?.company !== '' && form.invoice_address?.company"
                         class="mt-4 space-y-1">
                        <label
                            class="block font-medium text-sm text-gray-700 block text-sm font-medium text-gray-700">
                            {{ label('site.account.vat_number') }}
                            <span class="text-error-500" :title="label('required')">*</span>
                            <input
                                class="appearance-none border border-gray-300 p-2 rounded-md shadow-xs checked:bg-gray-500 checked:text-gray-500 disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed focus:border-gray-950 focus:ring-gray-950 block w-full sm:text-sm"
                                type="text" id="vat_number" name="vat_number" v-model="form.vat_number">
                        </label>
                        <Errors :errors="errors?.vat_number"/>
                    </div>
                </div>

                <!-- Shipping address -->
                <div>
                    <h2 class="text-lg font-bold text-gray-900 pb-4">
                        {{ label('site.cart.billing_information') }}
                    </h2>
                    <address-selector v-if="user" v-model="form.invoice_address" :countries="countries" type="invoice"
                                      :names="invoiceNames" :options="invoiceAddresses"/>
                    <AddressInput v-else v-model="form.invoice_address" :countries="countries"
                                  :show-address-inputs="true" :show-inputs="true" type="invoice" :names="invoiceNames"
                                  :errors="errors"></AddressInput>
                </div>

                <div class="mt-5 border-t border-gray-200 pt-5">
                    <h2 class="text-lg font-bold text-gray-900">
                        {{ label('site.cart.shipping_information') }}
                    </h2>
                    <div class="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                        <div class="sm:col-span-2">
                            <div class="flex space-x-2">
                                <div class="flex h-5 items-center">
                                    <input
                                        v-model="form.invoice_same_as_delivery"
                                        class="appearance-none border w-5 h-5 border-gray rounded-md shadow-xs checked:bg-black checked:text-black disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed focus:border-gray-500 focus:ring-gray-500  rounded-sm! shadow-none!"
                                        type="checkbox" id="invoice_same_as_delivery" name="invoice_same_as_delivery"
                                        value="1">
                                </div>
                                <label class="block font-medium text-sm text-gray-700"
                                       for="invoice_same_as_delivery"
                                       v-text="label('site.cart.invoice_same_as_delivery')">

                                </label>
                            </div>
                        </div>
                    </div>

                    <div v-if="form.invoice_same_as_delivery === false" class="py-2">
                        <address-selector v-if="user" v-model="form.delivery_address" :countries="countries"
                                          :names="deliveryNames" :options="deliveryAddresses" type="delivery"/>
                        <AddressInput v-else v-model="form.delivery_address" :countries="countries"
                                      :show-address-inputs="true" :show-inputs="true" :names="deliveryNames"
                                      :errors="errors"></AddressInput>
                    </div>

                    <div class="mt-5 border-t border-gray-200 pt-5">
                        <fieldset>
                            <legend class="text-lg font-bold text-gray-900">
                                {{ label('site.cart.delivery_method') }}
                            </legend>
                            <div class="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                                <input type="hidden" v-model="cartStore.deliveryMethod" name="delivery_method"
                                       id="delivery_method"/>
                                <label v-for="deliveryMethod in cartStore.deliveryMethods"
                                       :class="{
                                        'border-primary-700! border-2': cartStore.deliveryMethod == deliveryMethod.item_code
                                    }"
                                       @click="changeDeliveryMethod(deliveryMethod.item_code)"
                                       class="relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-xs focus:outline-hidden border-gray-300">
                                <span class="flex flex-1">
                                        <span class="flex flex-col">
                                            <span id="delivery-method-5-label"
                                                  class="block text-sm font-medium text-gray-900"
                                                  v-text="deliveryMethod.title">
                                            </span>
                                            <span id="delivery-method-5-description-0"
                                                  class="mt-1 flex items-center text-sm text-gray-500"
                                                  v-text="deliveryMethod.description">
                                            </span>
                                            <span id="delivery-method-5-description-1"
                                                  class="mt-6 text-sm font-medium text-gray-900"
                                                  v-if="deliveryMethod.price_incl && cartStore.cart">
                                                {{ Money.format(deliveryMethod.price_incl, cartStore.cart?.currency) }}
                                            </span>
                                        </span>
                                    </span>
                                    <svg aria-hidden="true" class="h-5 w-5 text-gray-600 hidden"
                                         xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fill-rule="evenodd"
                                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                                              clip-rule="evenodd"></path>
                                    </svg>
                                </label>
                            </div>
                            <Errors :errors="errors?.delivery_method"/>
                        </fieldset>
                    </div>

                    <div v-if="!user">
                        <div class="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                            <div class="sm:col-span-2">
                                <div class="flex space-x-2 py-2">
                                    <div class="flex h-5 items-center">
                                        <input
                                            v-model="form.create_account"
                                            class="appearance-none border w-5 h-5 border-gray-300 rounded-md shadow-xs checked:bg-black checked:text-gray-500 disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed focus:border-gray-500 focus:ring-gray-500  rounded-sm! shadow-none!"
                                            type="checkbox" id="create_account" name="create_account" value="1">
                                    </div>
                                    <label class="block font-medium text-sm text-gray-700" for="create_account"
                                           v-text="label('site.cart.create_account')">

                                    </label>
                                </div>
                                <div v-if="form.create_account"
                                     class="rounded-xl bg-gray-50 p-4 flex flex-col gap-4">
                                    <div v-text="label('site.cart.password.description')"
                                         class="items-center text-sm text-gray-500"></div>
                                    <input type="password" name="password" :placeholder="label('site.account.password')"
                                           class="appearance-none border border-gray-300 p-2 rounded-md shadow-xs checked:bg-gray-500 checked:text-gray-500 disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed focus:border-gray-950 focus:ring-gray-950 block w-full sm:text-sm">
                                    <input type="password" name="password_confirmation"
                                           :placeholder="label('site.account.password_confirmation')"
                                           class="appearance-none border border-gray-300 p-2 rounded-md shadow-xs checked:bg-gray-500 checked:text-gray-500 disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed focus:border-gray-950 focus:ring-gray-950 block w-full sm:text-sm">
                                    <Errors :errors="errors?.password"/>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mt-5 border-t border-gray-200 pt-5">
                    <h2 class="text-lg font-bold text-gray-900">
                        {{ label('site.cart.payment') }}
                    </h2>
                    <input type="hidden" name="payment_method" v-model="form.payment_method"/>
                    <fieldset class="mt-4" v-for="paymentOption in paymentMethods">
                        <div class="space-y-4">
                            <label
                                @click="form.payment_method = paymentOption.id"
                                class="relative block cursor-pointer rounded-lg border bg-white px-6 py-4 shadow-xs focus:outline-hidden sm:flex sm:justify-between border-gray-300"
                                :class="{
                                    'border-transparent ring-2 ring-gray-600': paymentOption.id === 'cash_on_delivery',
                                    'border-gray-300': paymentOption.id !== 'cash_on_delivery',
                                    'border-primary-700! border-2': form.payment_method == paymentOption.id
                                }"
                            >
                                <div class="flex items-center gap-3 w-full">
                                    <div class="w-2/12">
                                        <img class="max-w-10 w-full"
                                             :src="`data:image/svg+xml;base64, ${paymentOption.image}`"
                                             alt="">
                                    </div>
                                    <div class="flex flex-col text-sm w-10/12">
                                        <div class="font-medium text-sm text-gray-900"
                                             v-text="paymentOption.description">
                                        </div>
                                    </div>
                                </div>
                            </label>
                        </div>
                        <Errors :errors="errors?.payment_method"/>
                    </fieldset>
                </div>

                <!-- Notes -->
                <div class="mt-5 border-t border-gray-200 pt-5">
                    <div class="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                        <div class="sm:col-span-2">
                            <label class="block font-bold text-md text-gray-700" for="remarks">
                                {{ label('site.cart.remarks') }} <span
                                class="font-normal">({{ label('site.cart.optional') }})</span>
                            </label>
                            <div class="mt-3">
                                <textarea
                                    v-model="form.remarks"
                                    class="appearance-none border border-gray-300 p-2 rounded-md shadow-xs checked:bg-gray-500 checked:text-gray-500 disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed focus:border-gray-950 focus:ring-gray-950 block w-full sm:text-sm min-h-[140px]"
                                    name="remarks" id="remarks"
                                    placeholder="Opmerkingen over uw bestelling, bijvoorbeeld speciale opmerkingen over de levering."></textarea>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                    <div class="sm:col-span-2">
                        <div class="flex space-x-2">
                            <div class="flex h-5 items-center">
                                <input
                                    v-model="form.terms_of_service"
                                    class="appearance-none border w-5 h-5 border-gray-300 rounded-md shadow-xs checked:bg-black checked:text-gray-500 disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed focus:border-gray-500 focus:ring-gray-500  rounded-sm! shadow-none!"
                                    type="checkbox" id="terms_of_service" name="terms_of_service">
                            </div>
                            <label class="block font-medium text-sm text-gray-700" for="terms_of_service"
                                   v-text="label('site.cart.terms_of_service')">

                            </label>
                            <span class="text-error-500" :title="label('required')">*</span>
                        </div>
                        <Errors :errors="errors?.terms_of_service"/>
                    </div>
                </div>
            </div>


            <!-- Order summary -->
            <div class="mt-10 lg:mt-0">
                <div class="sticky top-8">
                    <h2 class="text-lg font-bold text-gray-900">
                        {{ label('site.cart.order_summary') }}
                    </h2>
                    <div class="mt-4 rounded-lg border border-gray-200 bg-white shadow-xs">
                        <CartSummary>
                            <template #actions>
                                <Skeleton class="h-12" v-if="cartStore.isLoading"/>
                                <button type="submit" v-else :disabled="!cartStore.items.length"
                                        class="font-medium text-base px-16 py-4 leading-6 text-white transition rounded-lg bg-primary hover:bg-primary-700 hover:duration-500 flex justify-center w-full">
                                    {{ label('site.cart.confirm_order') }}
                                </button>
                            </template>
                        </CartSummary>
                    </div>
                </div>
            </div>
        </form>
    </section>
</template>
